import {ContainerStatus, ContainerStatusEntity} from "../../../model/container-status";
import {createParamSelector, getUseQueryResultData} from "../../../util/selector-util";
import {createSelector} from "@reduxjs/toolkit";
import {ContainerLevelHistory, ContainerLevelHistoryEntity} from "../../../model/container-level-history";
import {
  ContainerLevelHistoryHolder,
  ContainerLevelHistoryHolderEntity
} from "../../../model/container-level-history-holder";
import {RestrictionType} from "../../../model/restriction";
import {isDatabaseCode} from "../../../model/database-code";
import {contains} from "../../../util/util";
import {isInDttmRange} from "../../../util/date-util";

export type ContainerStatusSelectorProps = {
  containerIds?: string[]
  companyId?: string
  operatingAreaId?: string
  routeGroupIds?: string[]
  routeIds?: string[]
  siteId?: string
  assignedCompanyId?: string
  unassignedOnly?: boolean
  transportViaPipeline?: boolean
  transportOilViaPipeline?: boolean
  usability?: "usable" | "unusable"
  restriction?: RestrictionType,
  showNoData?: boolean
  showDoNotUse?: boolean;
  highNonPrimaryVolumeOnly?: boolean
  criticalStatusOnly?: boolean
  availableLoadsOnly?: boolean
  minimumMeterHeight?: number
  lastDispatchedFromDttm?: number
  lastDispatchedToDttm?: number
  lastHauledFromDttm?: number
  lastHauledToDttm?: number
  neverDispatched?: boolean
  neverHauled?: boolean
};

const getContainerIdsParam = createParamSelector<ContainerStatusSelectorProps | undefined, string[] | undefined>(params => params?.containerIds);
const getCompanyIdParam = createParamSelector<ContainerStatusSelectorProps | undefined, string | undefined>(params => params?.companyId);
const getOperatingAreaIdParam = createParamSelector<ContainerStatusSelectorProps | undefined, string | undefined>(params => params?.operatingAreaId);
const getRouteGroupIdsParam = createParamSelector<ContainerStatusSelectorProps | undefined, string[] | undefined>(params => params?.routeGroupIds);
const getRouteIdsParam = createParamSelector<ContainerStatusSelectorProps | undefined, string[] | undefined>(params => params?.routeIds);
const getSiteIdParam = createParamSelector<ContainerStatusSelectorProps | undefined, string | undefined>(params => params?.siteId);
const getAssignedCompanyIdParam = createParamSelector<ContainerStatusSelectorProps | undefined, string | undefined>(params => params?.assignedCompanyId);
const getUnassignedOnlyParam = createParamSelector<ContainerStatusSelectorProps | undefined, boolean | undefined>(params => params?.unassignedOnly);
const getTransportViaPipelineParam = createParamSelector<ContainerStatusSelectorProps | undefined, boolean | undefined>(params => params?.transportViaPipeline);
const getTransportOilViaPipelineParam = createParamSelector<ContainerStatusSelectorProps | undefined, boolean | undefined>(params => params?.transportOilViaPipeline);
const getUsabilityParam = createParamSelector<ContainerStatusSelectorProps | undefined, "usable" | "unusable" | undefined>(params => params?.usability);
const getRestrictionParam = createParamSelector<ContainerStatusSelectorProps | undefined, RestrictionType | undefined>(params => params?.restriction);
const getShowNoDataParam = createParamSelector<ContainerStatusSelectorProps | undefined, boolean | undefined>(params => params?.showNoData);
const getShowDoNotUseParam = createParamSelector<ContainerStatusSelectorProps | undefined, boolean | undefined>(params => params?.showDoNotUse);
const getHighNonPrimaryVolumeOnlyParam = createParamSelector<ContainerStatusSelectorProps | undefined, boolean | undefined>(params => params?.highNonPrimaryVolumeOnly);
const getCriticalStatusOnlyParam = createParamSelector<ContainerStatusSelectorProps | undefined, boolean | undefined>(params => params?.criticalStatusOnly);
const getAvailableLoadsOnlyParam = createParamSelector<ContainerStatusSelectorProps | undefined, boolean | undefined>(params => params?.availableLoadsOnly);
const getMinimumMeterHeightParam = createParamSelector<ContainerStatusSelectorProps | undefined, number | undefined>(params => params?.minimumMeterHeight);
const getLastDispatchedFromDttmParam = createParamSelector<ContainerStatusSelectorProps | undefined, number | undefined>(p => p?.lastDispatchedFromDttm);
const getLastDispatchedToDttmParam = createParamSelector<ContainerStatusSelectorProps | undefined, number | undefined>(p => p?.lastDispatchedToDttm);
const getLastHauledFromDttmParam = createParamSelector<ContainerStatusSelectorProps | undefined, number | undefined>(p => p?.lastHauledFromDttm);
const getLastHauledToDttmParam = createParamSelector<ContainerStatusSelectorProps | undefined, number | undefined>(p => p?.lastHauledToDttm);
const getNeverDispatchedParam = createParamSelector<ContainerStatusSelectorProps | undefined, boolean | undefined>(p => p?.neverDispatched);
const getNeverHauledParam = createParamSelector<ContainerStatusSelectorProps | undefined, boolean | undefined>(p => p?.neverHauled);

export const containerStatusSelector = () => createSelector(
  [getUseQueryResultData<ContainerStatus>],
  (data) => {
    return data ? transformContainerStatus(data) : undefined;
  }
);

export const containerStatusesSelector = () => createSelector(
  [
    getUseQueryResultData<ContainerStatus[]>,
    getContainerIdsParam,
    getCompanyIdParam,
    getOperatingAreaIdParam,
    getRouteGroupIdsParam,
    getRouteIdsParam,
    getSiteIdParam,
    getAssignedCompanyIdParam,
    getUnassignedOnlyParam,
    getTransportViaPipelineParam,
    getTransportOilViaPipelineParam,
    getUsabilityParam,
    getRestrictionParam,
    getShowNoDataParam,
    getShowDoNotUseParam,
    getHighNonPrimaryVolumeOnlyParam,
    getCriticalStatusOnlyParam,
    getAvailableLoadsOnlyParam,
    getMinimumMeterHeightParam,
    getLastDispatchedFromDttmParam,
    getLastDispatchedToDttmParam,
    getLastHauledFromDttmParam,
    getLastHauledToDttmParam,
    getNeverDispatchedParam,
    getNeverHauledParam,
  ],
  (
    data,
    containerIds,
    companyId,
    operatingAreaId,
    routeGroupIds,
    routeIds,
    siteId,
    assignedCompanyId,
    unassignedOnly,
    transportViaPipeline,
    transportOilViaPipeline,
    usability,
    restriction,
    showNoData,
    showDoNotUse,
    highNonPrimaryVolumeOnly,
    criticalStatusOnly,
    availableLoadsOnly,
    minimumMeterHeight,
    lastDispatchedFromDttm,
    lastDispatchedToDttm,
    lastHauledFromDttm,
    lastHauledToDttm,
    neverDispatched,
    neverHauled,
  ) => {
    return selectContainerStatusesAndTransform(
      data ?? [], {
        containerIds: containerIds,
        companyId: companyId,
        operatingAreaId: operatingAreaId,
        routeGroupIds: routeGroupIds,
        routeIds: routeIds,
        siteId: siteId,
        assignedCompanyId: assignedCompanyId,
        unassignedOnly: unassignedOnly,
        transportViaPipeline: transportViaPipeline,
        transportOilViaPipeline: transportOilViaPipeline,
        usability: usability,
        restriction: restriction,
        showNoData: showNoData,
        showDoNotUse: showDoNotUse,
        highNonPrimaryVolumeOnly: highNonPrimaryVolumeOnly,
        criticalStatusOnly: criticalStatusOnly,
        availableLoadsOnly: availableLoadsOnly,
        minimumMeterHeight: minimumMeterHeight,
        lastDispatchedFromDttm: lastDispatchedFromDttm,
        lastDispatchedToDttm: lastDispatchedToDttm,
        lastHauledFromDttm: lastHauledFromDttm,
        lastHauledToDttm: lastHauledToDttm,
        neverDispatched: neverDispatched,
        neverHauled: neverHauled,
      }
    );
  }
);

export const containerStatusEntitiesSelector = () => createSelector(
  [
    getUseQueryResultData<ContainerStatusEntity[]>,
    getContainerIdsParam,
    getCompanyIdParam,
    getOperatingAreaIdParam,
    getRouteGroupIdsParam,
    getRouteIdsParam,
    getSiteIdParam,
    getAssignedCompanyIdParam,
    getUnassignedOnlyParam,
    getTransportViaPipelineParam,
    getTransportOilViaPipelineParam,
    getUsabilityParam,
    getRestrictionParam,
    getShowNoDataParam,
    getShowDoNotUseParam,
    getHighNonPrimaryVolumeOnlyParam,
    getCriticalStatusOnlyParam,
    getAvailableLoadsOnlyParam,
    getMinimumMeterHeightParam,
    getLastDispatchedFromDttmParam,
    getLastDispatchedToDttmParam,
    getLastHauledFromDttmParam,
    getLastHauledToDttmParam,
    getNeverDispatchedParam,
    getNeverHauledParam,
  ],
  (
    data,
    containerIds,
    companyId,
    operatingAreaId,
    routeGroupIds,
    routeIds,
    siteId,
    assignedCompanyId,
    unassignedOnly,
    transportViaPipeline,
    transportOilViaPipeline,
    usability,
    restriction,
    showNoData,
    showDoNotUse,
    highNonPrimaryVolumeOnly,
    criticalStatusOnly,
    availableLoadsOnly,
    minimumMeterHeight,
    lastDispatchedFromDttm,
    lastDispatchedToDttm,
    lastHauledFromDttm,
    lastHauledToDttm,
    neverDispatched,
    neverHauled,
  ) => {
    return selectContainerStatuses(
      data ?? [], {
        containerIds: containerIds,
        companyId: companyId,
        operatingAreaId: operatingAreaId,
        routeGroupIds: routeGroupIds,
        routeIds: routeIds,
        siteId: siteId,
        assignedCompanyId: assignedCompanyId,
        unassignedOnly: unassignedOnly,
        transportViaPipeline: transportViaPipeline,
        transportOilViaPipeline: transportOilViaPipeline,
        usability: usability,
        restriction: restriction,
        showNoData: showNoData,
        showDoNotUse: showDoNotUse,
        highNonPrimaryVolumeOnly: highNonPrimaryVolumeOnly,
        criticalStatusOnly: criticalStatusOnly,
        availableLoadsOnly: availableLoadsOnly,
        minimumMeterHeight: minimumMeterHeight,
        lastDispatchedFromDttm: lastDispatchedFromDttm,
        lastDispatchedToDttm: lastDispatchedToDttm,
        lastHauledFromDttm: lastHauledFromDttm,
        lastHauledToDttm: lastHauledToDttm,
        neverDispatched: neverDispatched,
        neverHauled: neverHauled,
      }
    );
  }
);

export const transformContainerStatus = (value: ContainerStatus): ContainerStatusEntity => {
  return ContainerStatusEntity.from(value)
}

export function selectContainerStatuses<T extends ContainerStatus | ContainerStatusEntity>(values?: T[], selector?: ContainerStatusSelectorProps): T[] {
  if (!values) {
    return [];
  }

  return values.filter((v) => {
    let filterIn = true;

    if (filterIn && selector?.containerIds && selector.containerIds.length > 0) {
      if (!contains(selector.containerIds, id => id === v.containerId)) {
        filterIn = false;
      }
    }

    if (selector?.availableLoadsOnly && selector.availableLoadsOnly) {
      if (!v.meterFill || !v.meterFill.availableLoadCount) {
        filterIn = false
      }
    }

    if (selector?.highNonPrimaryVolumeOnly && selector.highNonPrimaryVolumeOnly) {
      if (!v.meterFill || !v.meterFill.exceedsMaxNonPrimaryVolume) {
        filterIn = false
      }
    }

    if (selector?.criticalStatusOnly && selector.criticalStatusOnly) {
      if (!v.meterFill || v.meterFill?.actualStatus !== "CRITICAL") {
        filterIn = false
      }
    }

    if (selector?.minimumMeterHeight) {
      const meterHeight = v.meterFill?.actualHeight

      if (typeof meterHeight === 'undefined') {
        filterIn = false
      }

      if (meterHeight! < selector.minimumMeterHeight) {
        filterIn = false
      }
    }

    if (filterIn && selector?.companyId) {
      if (selector.companyId !== v.siteCompanyId) {
        filterIn = false
      }
    }

    if (filterIn && selector?.operatingAreaId) {
      if (selector.operatingAreaId !== v.siteOperatingAreaId) {
        filterIn = false
      }
    }

    if (filterIn && selector?.routeGroupIds) {
      if (!contains(selector.routeGroupIds, id => id === v.siteRouteGroupId)) {
        filterIn = false;
      }
    }

    if (filterIn && selector?.routeIds) {
      if (!contains(selector.routeIds, id => id === v.siteRouteId)) {
        filterIn = false;
      }
    }

    if (filterIn && selector?.siteId) {
      if (selector.siteId !== v.siteId) {
        filterIn = false
      }
    }

    if (selector?.unassignedOnly) {
      if (typeof v.assignedCompanyId !== 'undefined') {
        filterIn = false
      }
    } else if (filterIn && selector?.assignedCompanyId) {
      if (selector.assignedCompanyId !== v.assignedCompanyId) {
        filterIn = false
      }
    }

    if (typeof selector?.transportViaPipeline !== 'undefined') {
      if (selector.transportViaPipeline) {
        if (!v.siteTransportViaPipeline) {
          filterIn = false
        }
      } else {
        if (v.siteTransportViaPipeline) {
          filterIn = false
        }
      }
    }

    if (typeof selector?.transportOilViaPipeline !== 'undefined') {
      if (selector.transportOilViaPipeline) {
        if (!v.siteTransportOilViaPipeline) {
          filterIn = false
        }
      } else {
        if (v.siteTransportOilViaPipeline) {
          filterIn = false
        }
      }
    }

    if (filterIn && selector?.usability) {
      if (selector.usability === 'usable') {
        if (v.containerDoNotDispatch) {
          filterIn = false
        }
      } else {
        if (typeof v.containerDoNotDispatch !== "undefined" && !v.containerDoNotDispatch) {
          filterIn = false
        }
      }
    }

    if (filterIn && selector?.restriction) {
      const index = v.restrictions?.findIndex((value, index) => {
        if (isDatabaseCode(value.type)) {
          return value.type === selector.restriction
        } else {
          return value.type === selector.restriction?.code
        }
      })

      if (typeof index === 'undefined' || index < 0) {
        filterIn = false
      }
    }

    const showNoData = selector?.showNoData ?? true;
    if (filterIn && !showNoData && (!v.meterFill || !v.meterFill.dttm)) {
      filterIn = false;
    }

    const showDoNotUse = selector?.showDoNotUse ?? true;
    if (filterIn && !showDoNotUse && v.containerDoNotUse) {
      filterIn = false;
    }

    if (filterIn && selector?.neverDispatched) {
      const dttm = v.latestDispatchDttm;
      if (typeof dttm !== "undefined") {
        filterIn = false;
      }
    } else if (filterIn && (typeof selector?.lastDispatchedFromDttm !== "undefined" || typeof selector?.lastDispatchedToDttm !== "undefined")) {
      const dttm = v.latestDispatchDttm;
      if (typeof dttm === "undefined" || !isInDttmRange(dttm, selector.lastDispatchedFromDttm, selector.lastDispatchedToDttm)) {
        filterIn = false;
      }
    }

    if (filterIn && selector?.neverHauled) {
      const dttm = v.latestPickupDttm;
      if (typeof dttm !== "undefined") {
        filterIn = false;
      }
    } else if (filterIn && (typeof selector?.lastHauledFromDttm !== "undefined" || typeof selector?.lastHauledToDttm !== "undefined")) {
      const dttm = v.latestPickupDttm;
      if (typeof dttm === "undefined" || !isInDttmRange(dttm, selector.lastHauledFromDttm, selector.lastHauledToDttm)) {
        filterIn = false;
      }
    }

    return filterIn;
  });
}

export const selectContainerStatusesAndTransform = (values?: ContainerStatus[], selector?: ContainerStatusSelectorProps): ContainerStatusEntity[] => {
  const selected = selectContainerStatuses(values, selector);
  return selected.map(transformContainerStatus);
};

export type ContainerLevelSelectorProps = {
  // selector props here
};

export const containerLevelSelector = () => createSelector(
  [getUseQueryResultData<ContainerLevelHistory>],
  (data) => {
    return data ? transformContainerLevel(data) : undefined;
  }
);

export const containerLevelsSelector = () => createSelector(
  [getUseQueryResultData<ContainerLevelHistory[]>],
  (data) => {
    return selectContainerLevelsAndTransform(
      data ?? [], {}
    );
  }
);

export const containerLevelHolderSelector = () => createSelector(
  [getUseQueryResultData<ContainerLevelHistoryHolder>],
  (data) => {
    return data ? ContainerLevelHistoryHolderEntity.from(data) : undefined
  }
);

export const transformContainerLevel = (value: ContainerLevelHistory): ContainerLevelHistoryEntity => {
  return ContainerLevelHistoryEntity.from(value)
}

export const selectContainerLevels = (values?: ContainerLevelHistory[], selector?: ContainerLevelSelectorProps): ContainerLevelHistory[] => {
  if (!values) {
    return [];
  }

  return values.filter((v) => {
    let filterIn = true;
    return filterIn;
  });
};

export const selectContainerLevelsAndTransform = (values?: ContainerLevelHistory[], selector?: ContainerLevelSelectorProps): ContainerLevelHistoryEntity[] => {
  const selected = selectContainerLevels(values, selector);
  return selected.map(transformContainerLevel);
};
